import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Loader2, Search, Mic, Trash2, Pencil, FileText } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import VoiceRecorder from '../components/VoiceRecorder';
import SmartApprovalModal from '../components/SmartApprovalModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  DRAFT: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  REVIEW_LATER: 'bg-blue-100 text-blue-800 border-blue-300',
  APPROVED: 'bg-green-100 text-green-800 border-green-300',
  ARCHIVED: 'bg-slate-100 text-slate-700 border-slate-300',
};

export default function VoiceNotesPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('ALL');
  const [active, setActive] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (status !== 'ALL') params.status = status;
      const { data } = await axios.get(`${API_URL}/api/dashboard/voice-notes`, { headers, params });
      setNotes(data || []);
    } catch {
      toast.error('Could not load notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const openReview = (note) => { setActive(note); setModalOpen(true); };

  const del = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/dashboard/voice-notes/${id}`, { headers });
      toast.success('Note deleted');
      load();
    } catch {
      toast.error('Could not delete');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="voice-notes-title">
            <Mic className="h-6 w-6 text-primary" /> Voice Notes
          </h1>
          <VoiceRecorder variant="button" onSaved={load} />
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search notes…"
              className="pl-9" data-testid="voice-notes-search" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="sm:w-48" data-testid="voice-notes-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All notes</SelectItem>
              <SelectItem value="DRAFT">Needs review</SelectItem>
              <SelectItem value="REVIEW_LATER">Review later</SelectItem>
              <SelectItem value="APPROVED">Approved</SelectItem>
              <SelectItem value="ARCHIVED">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="h-7 w-7 animate-spin" />
          </div>
        ) : notes.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center space-y-2" data-testid="voice-notes-empty">
              <FileText className="h-8 w-8 mx-auto text-muted-foreground" />
              <p className="text-muted-foreground">No voice notes yet.</p>
              <p className="text-sm text-muted-foreground">Tap "Voice note" to record your first one.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3" data-testid="voice-notes-list">
            {notes.map((n) => (
              <Card key={n.id} data-testid={`voice-note-card-${n.id}`} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold truncate">{n.title || 'Voice note'}</p>
                        <Badge className={`${STATUS_BADGE[n.approvalStatus] || ''} border text-xs`}>
                          {n.approvalStatus === 'DRAFT' ? 'NEEDS REVIEW' : n.approvalStatus?.replace('_', ' ')}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-3">{n.transcription}</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {n.createdAt ? format(parseISO(n.createdAt), 'MMM d, yyyy h:mm a') : ''}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <Button size="sm" variant="outline" onClick={() => openReview(n)}
                        data-testid={`review-note-${n.id}`}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Review
                      </Button>
                      <Button size="sm" variant="ghost" className="text-destructive"
                        onClick={() => del(n.id)} data-testid={`delete-note-${n.id}`}>
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <SmartApprovalModal note={active} open={modalOpen} onOpenChange={setModalOpen} onApproved={load} />
    </DashboardLayout>
  );
}
